import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import { useIsMobile } from '../hooks/useIsMobile';
import { useLanguage } from '../contexts/LanguageContext';
import DesktopLayout from '../components/Desktop/DesktopLayout';
import MobileLayout from '../components/Mobile/MobileLayout';
import { searchEvents } from '../services/searchEvents';
import { useBehaviorTracking } from '../hooks/useBehaviorTracking';
import WelcomeModal from '../components/common/WelcomeModal';
import { NAV_TABS } from '../utils/categoriesConfig';
import { SEARCH_CATEGORIES } from '../config/searchCategoriesConfig';
import { DEFAULT_MAP_CENTER, boundsFromCenter } from '../utils/mapDefaults';
import { parseReviewerShareParams, REVIEWER_SHARE_PARAM_KEYS } from '../utils/reviewerShare';
import { splitItemId } from '../utils/itemShare';
import { useSharedItem } from '../hooks/useSharedItem';

// Helper function for deep bounds equality check with tolerance
const areBoundsEqual = (bounds1, bounds2, tolerance = 0.0001) => {
  if (!bounds1 || !bounds2) return false;
  if (!bounds1.ne || !bounds1.sw || !bounds2.ne || !bounds2.sw) return false;

  return (
    Math.abs(bounds1.ne.lat - bounds2.ne.lat) < tolerance &&
    Math.abs(bounds1.ne.lng - bounds2.ne.lng) < tolerance &&
    Math.abs(bounds1.sw.lat - bounds2.sw.lat) < tolerance &&
    Math.abs(bounds1.sw.lng - bounds2.sw.lng) < tolerance
  );
};
const DEFAULT_LAT = DEFAULT_MAP_CENTER.lat;
const DEFAULT_LNG = DEFAULT_MAP_CENTER.lng;

// How long to wait for the map to report its own bounds before synthesizing
// them. The map normally emits bounds ~300 ms after MapKit finishes loading.
const BOUNDS_WATCHDOG_MS = 4000;

// How long a shared-item link may hold up the first paint while we look up the
// item's coordinates. The ordinary geolocation path runs underneath, so this
// only ever shortens the wait — it never becomes the reason the page is blank.
const SHARE_BOOT_TIMEOUT_MS = 4000;

const Home = () => {
  const [places, setPlaces] = useState([]);
  const [filteredPlaces, setFilteredPlaces] = useState([]);
  const [childClicked, setChildClicked] = useState(null);
  const [coordinates, setCoordinates] = useState({ lat: DEFAULT_LAT, lng: DEFAULT_LNG });
  const [bounds, setBounds] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [type, setType] = useState('Explore');
  const [subCategory, setSubCategory] = useState(''); // NEW: Sub-category filter
  const [searchQuery, setSearchQuery] = useState(''); // NEW: Search query to pass to SmartEventList
  const [categoryFilter, setCategoryFilter] = useState(''); // NEW: Search bar category dropdown state

  // Initialize dates: start from 1 Jan 2025, end at today + 180 days
  const [startDate, setStartDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 180);
    return d.toISOString().split('T')[0];
  });

  // Router (used for the shared-reviewer deep link below)
  const routerLocation = useLocation();
  const navigate = useNavigate();

  // Shared item link (?item=…). Read from the URL on every render and never
  // copied into state — the recipient of a link has none of the sender's
  // state, so the URL has to be the thing that decides what is on screen.
  const { sharedId } = useSharedItem();

  // Responsive detection and language context
  const isMobile = useIsMobile();
  const { language } = useLanguage();

  // Behavior tracking for search
  const { trackSearch } = useBehaviorTracking('home_search');

  // Refs for abort controller, debounce timeout, and bounds memo
  const abortControllerRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const lastBoundsRef = useRef(null);
  const hasDoneInitialFetchRef = useRef(false); // Track if we've done the initial fetch
  const lastLanguageRef = useRef(language); // Track last language to detect changes

  // New layout state variables
  // A shared item opens on the รายการ (list) tab, which is where the expanded
  // overlay lives. Read once from the URL at mount; not carried state.
  const [currentView, setCurrentView] = useState(sharedId ? 'verticalExplore' : 'explore');
  const [isMapEnlarged, setIsMapEnlarged] = useState(() =>
    localStorage.getItem('mapEnlarged') === 'true'
  );
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [followList, setFollowList] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [feedCoordinates, setFeedCoordinates] = useState(null); // Map center at last explicit search
  const [feedRadiusKm, setFeedRadiusKm] = useState(null);       // radius that matched in progressive expansion
  const geolocationResolvedRef = useRef(false); // allow one re-fetch after geolocation resolves
  // Set once a shared link has centred the map on its item. A GPS fix that
  // lands afterwards must not yank the map to the visitor's own city while
  // they are looking at the item someone sent them.
  const shareCentreLockRef = useRef(false);
  const locationReadyRef = useRef(false);
  const [locationReady, setLocationReady] = useState(false);

  // NEW: Mobile search UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Creator profile states
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [creatorProfile, setCreatorProfile] = useState(null);
  // Category filter handed over by a shared reviewer link (?rf=…). Cleared as
  // soon as the visitor picks a different reviewer, so it never sticks.
  const [sharedReviewerFilter, setSharedReviewerFilter] = useState(null);

  // Clicking a creator/reviewer name (map info box or list card) opens the
  // ผู้รีวิว tab and shows everything that person published.
  const handleCreatorClick = useCallback((creator) => {
    console.log('👤 Creator clicked:', creator);
    setSharedReviewerFilter(null);
    setSelectedCreator(creator);
    setType('Reviewers');
    setCurrentView('verticalExplore');
    setSearchQuery('');
  }, []);

  // Selecting a reviewer from inside the ผู้รีวิว tab (suggestion strip or
  // search results) — same state change, without touching the view mode.
  const handleReviewerSelect = useCallback((reviewer) => {
    setSharedReviewerFilter(null);
    setSelectedCreator(reviewer || null);
  }, []);

  // Opening a shared reviewer link (?reviewer=42&rn=…&rf=events):
  // select that reviewer, apply the shared filter, then tidy the address bar.
  // The map needs nothing from the URL — it fits itself around the reviewer's
  // items (Map.jsx, fitToPlacesKey), so the visitor sees the same region.
  const sharedReviewerAppliedRef = useRef(false);
  useEffect(() => {
    if (sharedReviewerAppliedRef.current) return;

    const shared = parseReviewerShareParams(routerLocation.search);
    if (!shared) return;
    sharedReviewerAppliedRef.current = true;

    console.log('🔗 [Home] opening shared reviewer link:', shared);
    setSelectedCreator({ id: shared.reviewerId, name: shared.reviewerName || null });
    setSharedReviewerFilter(shared.filter);
    setType('Reviewers');
    setCurrentView('verticalExplore');
    setSearchQuery('');

    const params = new URLSearchParams(routerLocation.search);
    REVIEWER_SHARE_PARAM_KEYS.forEach((key) => params.delete(key));
    const search = params.toString();
    navigate(
      { pathname: routerLocation.pathname, search: search ? `?${search}` : '' },
      { replace: true }
    );
  }, [routerLocation.search, routerLocation.pathname, navigate]);

  // Leaving the ผู้รีวิว tab clears the reviewer so other tabs are unfiltered
  useEffect(() => {
    if (type !== 'Reviewers' && selectedCreator) {
      setSelectedCreator(null);
    }
  }, [type, selectedCreator]);

  useEffect(() => {
    if (!selectedCreator?.id) {
      setCreatorProfile(null);
      return;
    }
    
    const fetchCreatorProfile = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/users/${selectedCreator.id}`);
        if (response.ok) {
          const data = await response.json();
          setCreatorProfile(data);
        }
      } catch (err) {
        console.warn('Could not fetch creator profile:', err);
      }
    };
    
    fetchCreatorProfile();
  }, [selectedCreator]);

  // Derive search params from current tab
  const getTabSearchParams = useCallback((tabId) => {
    const tab = NAV_TABS.find(t => t.id === tabId);
    if (!tab) {
      // Fallback for unknown tab
      return { source: ['events'], excludeCategories: [], categoryFilter: null };
    }
    return {
      source: tab.sources,
      excludeCategories: tab.excludeCategories,
      categoryFilter: tab.categoryFilter
    };
  }, []);

  // Merge NAV_TAB params with category dropdown override.
  // When a SEARCH_CATEGORIES entry is selected its source/categoryFilter
  // override whatever the active NAV_TAB would produce.
  const getEffectiveSearchParams = useCallback((tabId, catFilter) => {
    const tabParams = getTabSearchParams(tabId);
    const selectedCat = SEARCH_CATEGORIES.find(c => c.value === catFilter);
    if (selectedCat) {
      return {
        source: selectedCat.source,
        excludeCategories: [],
        categoryFilter: selectedCat.categoryFilter,
      };
    }
    return tabParams;
  }, [getTabSearchParams]);

  const tabParams = getEffectiveSearchParams(type, categoryFilter);

  // Welcome modal state - show only once (first time user visits)
  const [showWelcomeModal, setShowWelcomeModal] = useState(() => {
    const hasCompletedOnboarding = localStorage.getItem('tvent_onboarding_completed');
    return !hasCompletedOnboarding; // Show only if user hasn't completed onboarding
  });

  // Legacy state (keep for backward compatibility) - will be removed after testing

  // Persist map enlargement state
  useEffect(() => {
    localStorage.setItem('mapEnlarged', isMapEnlarged);
  }, [isMapEnlarged]);


  // Get user location before rendering the map.
  // Wait up to 3 s for GPS; if it arrives in time the map opens directly on the user.
  // If GPS is slower, fall back to Bangkok after 3 s and re-center once GPS resolves.
  useEffect(() => {
    const markReady = (loc) => {
      if (loc) {
        // Keep the visitor's own position for distances and the "you are here"
        // marker, but leave the centre alone when a shared link owns it.
        if (!shareCentreLockRef.current) setCoordinates(loc);
        setUserLocation(loc);
      }
      if (!locationReadyRef.current) {
        locationReadyRef.current = true;
        setLocationReady(true);
      } else {
        // Timer already fired with Bangkok; GPS arrived late — allow Effect #1 to re-fetch
        geolocationResolvedRef.current = true;
      }
    };

    // Fallback: render with Bangkok after 5 s if GPS hasn't arrived yet
    const timer = setTimeout(() => {
      if (!locationReadyRef.current) {
        console.log('⏱️ GPS timeout — falling back to Bangkok');
        locationReadyRef.current = true;
        setLocationReady(true);
      }
    }, 5000);

    // navigator.geolocation is absent in insecure contexts and in some
    // WebViews. Calling into it unguarded threw from inside this effect, which
    // took the whole page down instead of quietly falling back.
    if (!navigator.geolocation) {
      console.warn('⚠️ Geolocation API unavailable — falling back to default centre');
      clearTimeout(timer);
      markReady(null);
      return () => clearTimeout(timer);
    }

    try {
      navigator.geolocation.getCurrentPosition(
        ({ coords: { latitude, longitude } }) => {
          console.log('🌍 User location obtained:', { latitude, longitude });
          clearTimeout(timer);
          markReady({ lat: latitude, lng: longitude });
        },
        (error) => {
          console.warn('⚠️ Geolocation error:', error.message);
          clearTimeout(timer);
          markReady(null); // fall back to Bangkok
        },
        { timeout: 30000, maximumAge: 60000 }
      );
    } catch (err) {
      console.warn('⚠️ Geolocation threw synchronously:', err);
      clearTimeout(timer);
      markReady(null);
    }

    return () => clearTimeout(timer);
  }, []);

  // Shared link cold start.
  //
  // Normally nothing renders until geolocation resolves (up to 5 s) and the map
  // then opens on the visitor. Someone who followed a link wants the item they
  // were sent, not a GPS prompt, so: look the item up, centre on it, and lift
  // the render gate as soon as we have an answer. This races the geolocation
  // path rather than replacing it — if the lookup is slow, hangs, or the item
  // is gone, the original 5 s fallback still fires and Home renders as usual
  // (the overlay then reports the item as unavailable).
  useEffect(() => {
    if (!sharedId) return undefined;

    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) return undefined;

    const { isReview, numericId } = splitItemId(sharedId);
    const controller = new AbortController();
    let cancelled = false;
    const timeoutId = setTimeout(() => controller.abort(), SHARE_BOOT_TIMEOUT_MS);

    const releaseGate = () => {
      if (cancelled || locationReadyRef.current) return;
      locationReadyRef.current = true;
      setLocationReady(true);
    };

    fetch(`${apiUrl}/${isReview ? 'reviews' : 'events'}/${numericId}`, {
      signal: controller.signal,
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((row) => {
        if (cancelled || !row) return;
        const lat = parseFloat(row.latitude);
        const lng = parseFloat(row.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

        console.log('\ud83d\udd17 [Home] centring on shared item:', sharedId, { lat, lng });
        shareCentreLockRef.current = true;
        setCoordinates({ lat, lng });
        // Give the feed the same centre, so what is behind the overlay is what
        // is near the shared item rather than whatever is near the visitor.
        setFeedCoordinates((prev) => prev || { lat, lng });
      })
      .catch(() => {
        // Deleted, unpublished, offline, or aborted by the timeout. Nothing to
        // do here: EventExpandedView surfaces the failure to the user.
      })
      .finally(() => {
        clearTimeout(timeoutId);
        releaseGate();
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [sharedId]);

  // BUGFIX (blank Home on first load without location permission):
  // The recommendation feed is gated on feedCoordinates — MobileLayout passes
  // `enabled={feedLat != null}` to SmartEventList, and while that is false the
  // list renders a spinner that never resolves. feedCoordinates used to be set
  // only as a side effect of a successful fetchAndUpdate, which itself needs
  // map bounds. So the feed hung off this whole chain:
  //     geolocation → locationReady → MapKit loads → map emits bounds → search
  // Any break in it (permission denied, GPS never answering, MapKit blocked)
  // left the user with the page chrome and nothing in it.
  //
  // Seed the feed centre as soon as we know where to look. `prev ||` means a
  // real search still owns the value afterwards; this only fills the gap.
  useEffect(() => {
    if (!locationReady) return;
    const center = userLocation || coordinates;
    if (center?.lat == null || center?.lng == null) return;

    setFeedCoordinates((prev) => {
      if (prev) return prev;
      console.log('[Home] 🌱 Seeding feed centre before first map search:', center);
      return { lat: center.lat, lng: center.lng };
    });
  }, [locationReady, userLocation, coordinates]);

  // Safety net for the other half of that chain: if the map never reports
  // bounds (MapKit failed to load, token rejected, zero-size container), the
  // headless search never runs and the map shows no markers. Synthesize a
  // viewport around the current centre so the search still happens.
  useEffect(() => {
    if (!locationReady || bounds) return undefined;

    const timer = setTimeout(() => {
      const synthetic = boundsFromCenter(userLocation || coordinates);
      if (!synthetic) return;
      console.warn(
        `[Home] ⏱️ No map bounds after ${BOUNDS_WATCHDOG_MS}ms — using synthetic bounds`,
        synthetic
      );
      synthetic._src = 'synthetic-fallback';
      setBounds((prev) => prev || synthetic);
    }, BOUNDS_WATCHDOG_MS);

    return () => clearTimeout(timer);
  }, [locationReady, bounds, userLocation, coordinates]);

  // Helper function to get focused item identifier
  const getFocusedItemId = useCallback((clickedIndex, placesList) => {
    if (clickedIndex === null || clickedIndex === undefined || !Array.isArray(placesList)) {
      return null;
    }
    const focusedItem = placesList[clickedIndex];
    return focusedItem?.id || focusedItem?.event_name || null;
  }, []);

  // Helper function to restore focus by id or index
  const restoreFocus = useCallback((prevFocusedId, prevFocusedIndex, newPlacesList) => {
    if (!Array.isArray(newPlacesList) || newPlacesList.length === 0) {
      return null;
    }

    // Try to find by stable ID first
    if (prevFocusedId) {
      const foundIndex = newPlacesList.findIndex(place =>
        place.id === prevFocusedId || place.event_name === prevFocusedId
      );
      if (foundIndex !== -1) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🎯 Focus preserved by ID:', { prevFocusedId, newIndex: foundIndex });
        }
        return foundIndex;
      }
    }

    // Fallback to same index if in range
    if (typeof prevFocusedIndex === 'number' &&
        prevFocusedIndex >= 0 &&
        prevFocusedIndex < newPlacesList.length) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🎯 Focus preserved by index:', { prevFocusedIndex });
      }
      return prevFocusedIndex;
    }

    // Focus lost - item disappeared and index out of range
    if (process.env.NODE_ENV === 'development') {
      console.log('⚠️ Focus lost - item not found:', { prevFocusedId, prevFocusedIndex, newLength: newPlacesList.length });
    }
    return null;
  }, []);

  // Fix: Enhanced updatePlaces function with better error handling, logging, and focus preservation
  const updatePlaces = useCallback(async (hits) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 updatePlaces called with:', hits);
    }
    setIsLoading(true);

    // Store previous focus state before updating places
    const prevFocusedId = getFocusedItemId(childClicked, places);
    const prevFocusedIndex = childClicked;

    try {
      if (!Array.isArray(hits)) {
        console.error('❌ updatePlaces: hits is not an array:', typeof hits);
        setPlaces([]);
        setChildClicked(null);
        return;
      }

      const validHits = hits.filter((hit) => {
        const isValid = hit &&
          (hit.event_name || hit.place_name) &&
          (hit.latitude !== null && hit.latitude !== undefined) &&
          (hit.longitude !== null && hit.longitude !== undefined);

        if (!isValid) {
          console.warn('⚠️ Invalid hit filtered out:', hit);
        }
        return isValid;
      });

      if (process.env.NODE_ENV === 'development') {
        console.log(`📍 Before focus preservation:`, { prevFocusedId, prevFocusedIndex });
        console.log(`📍 Updating places: ${hits.length} total → ${validHits.length} valid events`);
        console.log('📊 Sample valid events:', validHits.slice(0, 3));
      }

      setPlaces(validHits);

      // Re-apply focus after updating places
      const newFocusIndex = restoreFocus(prevFocusedId, prevFocusedIndex, validHits);
      setChildClicked(newFocusIndex);

      if (process.env.NODE_ENV === 'development') {
        console.log(`📍 After focus preservation:`, { newFocusIndex });
      }

    } catch (error) {
      console.error('💥 Error in updatePlaces:', error);
      setPlaces([]);
      setChildClicked(null);
    } finally {
      setIsLoading(false);
    }
  }, [childClicked, places, getFocusedItemId, restoreFocus]);

  // Headless search controller - fetches data without requiring Header to be mounted
  const fetchAndUpdate = useCallback(async (reason = 'unknown') => {
    // Early return if no bounds set
    if (!bounds) {
      console.log('🔎 fetchAndUpdate: No bounds set, skipping search. Reason:', reason);
      return;
    }

    // Cancel any previous search
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      console.log('🚫 fetchAndUpdate: Aborted previous search');
    }

    // Create new abort controller for this search
    abortControllerRef.current = new AbortController();
    const currentController = abortControllerRef.current;

    console.log('🏁 fetchAndUpdate started:', { reason, type, subCategory, startDate, endDate, language, bounds: !!bounds });

    try {
      setIsLoading(true);

      const currentTabParams = getEffectiveSearchParams(type, categoryFilter);
      const baseParams = {
        bounds,
        startDate,
        endDate,
        type: currentTabParams.categoryFilter || undefined,
        subCategory,
        query: '',
        userLocation,
        // BUGFIX: the progressive radius expansion below only does anything if
        // searchEvents has a centre to expand around. It used to key off
        // `userLocation` alone, so a user who denied location fell through to
        // the bounds-derived radius — which ignores radiusKm entirely, making
        // all five passes run the identical viewport query. Pass the map centre
        // as an explicit search centre so expansion works without GPS too.
        searchCenter: userLocation || coordinates,
        language,
        source: currentTabParams.source,
        excludeCategories: currentTabParams.excludeCategories,
        applyLocationFilterForAuto: true,
        apiUrl: process.env.REACT_APP_API_URL,
        signal: currentController.signal,
        logger: console
      };

      // Progressive radius expansion: 50 → 100 → 300 → 500 → 1000 km.
      // disableFallback=true prevents searchEvents from silently returning unfiltered
      // Bangkok results when 0 items exist in the current radius — we need genuine 0s
      // so the loop can expand to the next radius. Only the final 1000 km step allows
      // the fallback, guaranteeing the user always gets something.
      const radii = [50, 100, 300, 500, 1000];
      let events = [];
      for (const radius of radii) {
        if (currentController.signal.aborted) break;
        const isLast = radius === radii[radii.length - 1];
        events = await searchEvents({ ...baseParams, radiusKm: radius, disableFallback: !isLast });
        if (currentController.signal.aborted) break;
        if (events.length > 0) {
          console.log(`📍 fetchAndUpdate: found ${events.length} results at radius ${radius} km`);
          // Sync SmartEventList to the same location + winning radius so the list
          // shows the same area as the map markers.
          // FIX: fall back to map center when geolocation is unavailable/denied —
          // otherwise feedCoordinates stays null and SmartEventList never initializes
          // (enabled=false → infinite spinner).
          const feedCenter = userLocation || coordinates;
          if (feedCenter?.lat != null) {
            // Only move the feed center if it changed meaningfully (~1 km) —
            // otherwise keep the previous object so SmartEventList doesn't
            // reset and re-fetch the whole list for a negligible change.
            setFeedCoordinates(prev => {
              if (prev &&
                  Math.abs(prev.lat - feedCenter.lat) < 0.01 &&
                  Math.abs(prev.lng - feedCenter.lng) < 0.01) {
                return prev;
              }
              return { lat: feedCenter.lat, lng: feedCenter.lng };
            });
            setFeedRadiusKm(radius);
          }
          break;
        }
        console.log(`📍 fetchAndUpdate: 0 results at ${radius} km — expanding radius`);
      }

      // FIX: even if 0 events were found on the map, enable the feed list with the
      // map center so it can show its own results/empty state instead of an
      // infinite spinner.
      setFeedCoordinates(prev => prev || { lat: coordinates.lat, lng: coordinates.lng });

      // Check if search was aborted
      if (currentController.signal.aborted) {
        console.log('🚫 fetchAndUpdate: Search was cancelled');
        return;
      }

      console.log('🏁 fetchAndUpdate done:', { reason, count: events.length });

      // Update places using existing updatePlaces function
      updatePlaces(events);

      // Mark that a search has been completed
      setHasSearched(true);

    } catch (error) {
      if (error.name === 'AbortError' || currentController.signal.aborted) {
        console.log('🚫 fetchAndUpdate: Search was aborted');
        return;
      }

      console.error('💥 fetchAndUpdate error:', error);
      updatePlaces([]);
    } finally {
      setIsLoading(false);
    }
  }, [bounds, startDate, endDate, type, subCategory, userLocation, coordinates, language, updatePlaces, getEffectiveSearchParams, categoryFilter]);

  // Filter places by "type" and "selectedCreator"
  useEffect(() => {
    console.log('🏷️ Filtering places by type and selectedCreator:', { type, selectedCreator });
    
    let result = places;
    if (selectedCreator) {
      result = result.filter(p => {
        const creatorName = p._source === 'review'
          ? (p.reviewer_name || p.user_name || p.creator_name || '')
          : (p.creator_name || p.reviewer_name || p.organizer || p.user_name || '');
        const creatorId = p.event_creator_id || p.venue_creator_id || p.user_id || p.reviewer_id || '';
        return (
          (selectedCreator.name && creatorName.toLowerCase() === selectedCreator.name.toLowerCase()) ||
          (selectedCreator.id && String(creatorId) === String(selectedCreator.id))
        );
      });
    }
    
    setFilteredPlaces(result);
    console.log(`📊 Filtered Places: ${result.length} of ${places.length}`);
  }, [type, places, selectedCreator]);

  // Click handler for map location link
  const handleLocationClick = useCallback((place) => {
    if (!place?.event_location) return;
    const locationName = encodeURIComponent(place.event_location);
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${locationName}`;
    window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
  }, []);

  // Handle welcome modal close
  const handleCloseWelcomeModal = useCallback((selectedTags, lifestyleText) => {
    setShowWelcomeModal(false);
    localStorage.setItem('tvent_onboarding_completed', 'true');
    localStorage.setItem('tvent_onboarding_completed_at', new Date().toISOString());

    // TODO: Trigger permission check and save preferences
    // handleOnboardingComplete(selectedTags, lifestyleText);
  }, []);

  // Helper function to convert event ID to global index
  // Currently not used but kept for future ID-first selection enhancements
  const getGlobalIndexById = useCallback((eventId) => {
    const listToUse = filteredPlaces.length ? filteredPlaces : places;
    return listToUse.findIndex(p => p.id === eventId);
  }, [places, filteredPlaces]);

  // Marker click -> highlight on List (ID-first approach)
  const handleMarkerClick = useCallback(
    (indexOrPlaceId) => {
      // Handle both index (from navigation) and placeId (from marker clicks)
      if (indexOrPlaceId === null) {
        setChildClicked(null);
        console.log('📍 Selection cleared');
        return;
      }

      const listToUse = filteredPlaces.length ? filteredPlaces : places;

      // If it's a number within valid range, treat as index
      if (typeof indexOrPlaceId === 'number' &&
          indexOrPlaceId >= 0 &&
          indexOrPlaceId < listToUse.length) {
        setChildClicked(indexOrPlaceId);
        console.log('📍 Index selected:', { index: indexOrPlaceId });
        return;
      }

      // Otherwise, treat as placeId and find index
      const clickedIndex = listToUse.findIndex((p) => p.id === indexOrPlaceId);
      if (clickedIndex !== -1) {
        setChildClicked(clickedIndex);
        console.log('📍 Marker clicked:', { placeId: indexOrPlaceId, clickedIndex });
      } else {
        console.warn('⚠️ Place not found:', indexOrPlaceId);
      }
    },
    [places, filteredPlaces]
  );

  // Map centering function for view mode changes
  const centerOnFirstEvent = useCallback((events) => {
    if (events.length > 0) {
      const firstEvent = events[0];
      if (firstEvent.latitude && firstEvent.longitude) {
        setCoordinates({
          lat: firstEvent.latitude,
          lng: firstEvent.longitude
        });
        console.log('🎯 Centered map on first event:', firstEvent.event_name);
      }
    }
  }, []);

  // Handle view mode switching with data fetching
  const handleViewModeChange = useCallback(async (newMode) => {
    setCurrentView(newMode);
    console.log('🔄 View mode changed to:', newMode);
    
    // For now, we'll use the existing updatePlaces function
    // In the future, you can add specific API calls for each mode:
    // - following: fetch events from followed users
    // - explore: fetch recommended events (current behavior)
    // - nearby: fetch location-based events
    
    // Center map on first event after data loads (only if following list)
    if (followList) {
      setTimeout(() => {
        const currentPlaces = filteredPlaces.length ? filteredPlaces : places;
        centerOnFirstEvent(currentPlaces);
      }, 100);
    }
  }, [filteredPlaces, places, centerOnFirstEvent]);

  // onSearch handler for search overlay and map overlay - triggers manual search
  const handleSearch = useCallback(async (searchQuery = '') => {
    console.log('🔍 handleSearch called:', { searchQuery, bounds: !!bounds, language });

    // NEW: Set search query to trigger SmartEventList refresh
    setSearchQuery(searchQuery);
    // Update feed geo to current map center so the list searches the visible area
    setFeedCoordinates({ lat: coordinates.lat, lng: coordinates.lng });
    // Force refresh by incrementing trigger (ensures re-fetch even if searchQuery is same)
    setRefreshTrigger(prev => prev + 1);

    if (!bounds) {
      console.log('⚠️ handleSearch: No bounds set, cannot search');
      return;
    }

    // Cancel any previous search
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      console.log('🚫 handleSearch: Aborted previous search');
    }

    // Create new abort controller for this search
    abortControllerRef.current = new AbortController();
    const currentController = abortControllerRef.current;

    try {
      setIsLoading(true);

      const currentTabParams = getEffectiveSearchParams(type, categoryFilter);
      const events = await searchEvents({
        bounds,
        startDate,
        endDate,
        type: currentTabParams.categoryFilter || undefined,
        subCategory,
        query: searchQuery || '', // Use provided search query
        userLocation,
        language,
        source: currentTabParams.source,
        excludeCategories: currentTabParams.excludeCategories,
        apiUrl: process.env.REACT_APP_API_URL,
        signal: currentController.signal,
        logger: console
      });

      // Check if search was aborted
      if (currentController.signal.aborted) {
        console.log('🚫 handleSearch: Search was cancelled');
        return;
      }

      console.log('✅ handleSearch done:', { searchQuery, count: events.length });

      // Update places using existing updatePlaces function
      updatePlaces(events);

      // Track successful search
      trackSearch(searchQuery || '', {
        category: type,
        startDate,
        endDate,
        language,
        hasBounds: !!bounds
      }, events);

      // Mark that a search has been completed
      setHasSearched(true);

    } catch (error) {
      if (error.name === 'AbortError' || currentController.signal.aborted) {
        console.log('🚫 handleSearch: Search was aborted');
        return;
      }

      console.error('💥 handleSearch error:', error);
      updatePlaces([]);
    } finally {
      setIsLoading(false);
    }
  }, [bounds, startDate, endDate, type, subCategory, userLocation, language, updatePlaces, trackSearch, setSearchQuery, getEffectiveSearchParams, categoryFilter]);

  // Effect #1: Initial fetch when bounds first become available
  useEffect(() => {
    if (!bounds) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🎯 Initial fetch effect: No bounds yet');
      }
      return;
    }

    // Debug log to see what's happening
    if (process.env.NODE_ENV === 'development') {
      console.log('[Home] effect triggered with', {
        bounds: !!bounds,
        coordinates,
        type,
        hasSearched,
        boundsSource: bounds._src,
        hasDoneInitialFetch: hasDoneInitialFetchRef.current,
      });
    }

    // OPTION B: Force the first fetch even if bounds change is non-user
    if (!hasDoneInitialFetchRef.current) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Home] 🚀 Forcing initial fetch (option B) because this is the first run');
      }
      lastBoundsRef.current = bounds;
      fetchAndUpdate('initial-fetch-forced');
      hasDoneInitialFetchRef.current = true;
      return;
    }

    // Skip if already identical to last bounds (layout remount protection)
    if (areBoundsEqual(lastBoundsRef.current, bounds)) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🎯 Initial fetch effect: Bounds identical to last bounds, skipping');
      }
      return;
    }

    // After first fetch, apply the old logic
    if (!hasSearched) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🎯 Initial fetch effect: First-time bounds, triggering initial fetch');
      }
      lastBoundsRef.current = bounds;
      fetchAndUpdate('initial-bounds');
    } else {
      // We have searched before, check if this is a user-initiated search
      if (bounds._src === 'user-search') {
        if (process.env.NODE_ENV === 'development') {
          console.log('🎯 Initial fetch effect: User-initiated search, triggering fetch');
        }
        lastBoundsRef.current = bounds;
        fetchAndUpdate('user-search');
      } else if (geolocationResolvedRef.current) {
        // Geolocation resolved after the initial Bangkok fetch; map has re-centered on user's
        // location and emitted new bounds — run one fetch with the correct area.
        geolocationResolvedRef.current = false;
        if (process.env.NODE_ENV === 'development') {
          console.log('[Home] 📍 Re-fetching after geolocation resolved');
        }
        lastBoundsRef.current = bounds;
        fetchAndUpdate('geolocation-resolved');
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('[Home] 🛑 Skipping fetch: non-user bounds AND initial fetch already done');
        }
        // Still update last bounds to prevent future duplicate calls
        lastBoundsRef.current = bounds;
      }
    }
  }, [bounds, hasSearched, coordinates, type]); // Removed fetchAndUpdate to break circular dependency

  // Effect #2: Immediate fetch when language changes
  useEffect(() => {
    // Skip on initial mount or if language hasn't actually changed
    if (!bounds || !hasDoneInitialFetchRef.current) {
      lastLanguageRef.current = language;
      return;
    }

    // Only trigger if language actually changed
    if (lastLanguageRef.current !== language) {
      console.log('🌐 Language changed from', lastLanguageRef.current, 'to', language, '- triggering immediate fetch');
      lastLanguageRef.current = language;
      fetchAndUpdate('language-changed');
    }
  }, [language, bounds]); // fetchAndUpdate not included to avoid infinite loop

  // Effect #3: Debounced fetch when other filters change
  useEffect(() => {
    // Don't trigger on initial mount or if bounds not set yet
    if (!bounds) {
      return;
    }

    console.log('🔄 Filter change detected:', { type, subCategory, startDate, endDate });

    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Debounce filter changes by 400ms
    debounceTimeoutRef.current = setTimeout(() => {
      console.log('🏷️ Filters changed, triggering debounced fetch');
      fetchAndUpdate('filters-changed');
    }, 400);

    // Cleanup timeout on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [type, subCategory, startDate, endDate, categoryFilter]); // Removed language - handled by separate effect

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Debug logging for component state
  useEffect(() => {
    console.log('🏠 Home component state update:', {
      placesCount: places.length,
      filteredPlacesCount: filteredPlaces.length,
      type,
      coordinates,
      bounds: bounds ? 'Set' : 'Not set',
      isLoading
    });
  }, [places, filteredPlaces, type, coordinates, bounds, isLoading]);

  return (
    <Box
      className="home-page"
      sx={{
        height: { xs: 'var(--app-viewport-height)', md: '100vh' },
        width: '100%',
        overflow: 'hidden'
      }}
    >
      <CssBaseline />

      {/* Welcome Modal */}
      <WelcomeModal
        open={showWelcomeModal}
        onClose={handleCloseWelcomeModal}
      />

      {!locationReady ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'var(--app-viewport-height)' }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        <MobileLayout
          coordinates={coordinates}
          setCoordinates={setCoordinates}
          bounds={bounds}
          setBounds={setBounds}
          childClicked={childClicked}
          setChildClicked={setChildClicked}
          handleMarkerClick={handleMarkerClick}
          handleLocationClick={handleLocationClick}
          currentView={currentView}
          setCurrentView={handleViewModeChange}
          isMapEnlarged={isMapEnlarged}
          setIsMapEnlarged={setIsMapEnlarged}
          showMobileSearch={showMobileSearch}
          setShowMobileSearch={setShowMobileSearch}
          type={type}
          setType={setType}
          subCategory={subCategory}
          setSubCategory={setSubCategory}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          isSearchExpanded={isSearchExpanded}
          setIsSearchExpanded={setIsSearchExpanded}
          followList={followList}
          setFollowList={setFollowList}
          userLocation={userLocation}
          feedLat={feedCoordinates?.lat}
          feedLng={feedCoordinates?.lng}
          feedRadiusKm={feedRadiusKm}
          onSearch={handleSearch}
          searchQuery={searchQuery}
          hasSearched={hasSearched}
          source={tabParams.source}
          excludeCategories={tabParams.excludeCategories}
          categoryFilter={tabParams.categoryFilter}
          uiCategoryFilter={categoryFilter}
          setUiCategoryFilter={setCategoryFilter}
          selectedCreator={selectedCreator}
          setSelectedCreator={setSelectedCreator}
          creatorProfile={creatorProfile}
          onCreatorClick={handleCreatorClick}
          onReviewerSelect={handleReviewerSelect}
          initialReviewerFilter={sharedReviewerFilter}
        />
      ) : (
        <DesktopLayout
          coordinates={coordinates}
          setCoordinates={setCoordinates}
          bounds={bounds}
          setBounds={setBounds}
          childClicked={childClicked}
          setChildClicked={setChildClicked}
          handleMarkerClick={handleMarkerClick}
          handleLocationClick={handleLocationClick}
          currentView={currentView}
          setCurrentView={handleViewModeChange}
          type={type}
          setType={setType}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          followList={followList}
          setFollowList={setFollowList}
          userLocation={userLocation}
          feedLat={feedCoordinates?.lat}
          feedLng={feedCoordinates?.lng}
          feedRadiusKm={feedRadiusKm}
          onSearch={handleSearch}
          searchQuery={searchQuery}
          hasSearched={hasSearched}
          source={tabParams.source}
          excludeCategories={tabParams.excludeCategories}
          categoryFilter={tabParams.categoryFilter}
          uiCategoryFilter={categoryFilter}
          setUiCategoryFilter={setCategoryFilter}
          selectedCreator={selectedCreator}
          setSelectedCreator={setSelectedCreator}
          creatorProfile={creatorProfile}
          onCreatorClick={handleCreatorClick}
          onReviewerSelect={handleReviewerSelect}
          initialReviewerFilter={sharedReviewerFilter}
        />
      )}

    </Box>
  );
};

export default Home;
